import type {
  DateString,
  LotteryEvent,
  LotteryEventStatus,
  LotteryStockDoc,
} from '@kawai/shared';

/**
 * M-164: 抽選状況ページの集計（純関数）。
 *
 * 入力は lottery_events 1 件 + lottery_stock の等値 list（`tenantId == && eventId ==` で
 * 全部読む / index 不要）。lottery_stock は件数だけのカウンタ doc で、key（prizeId / date）で
 * 役割が分かれる（shared/src/lotteryTypes.ts の LotteryStockDoc 参照）:
 * - `{prizeId}` × `{date}` / `'total'` … 景品の当選数
 * - `'_draws'`  × `{date}` / `'total'` … 抽選数（count = win + miss）
 * - `'_outcome'` × `{date}`            … 券を消費しない結果の件数（キーごと）
 *
 * 「本日」は呼び出し側が todayJst() で決めて渡す（ここでは new Date() を触らない = テストで
 * Date を偽装しなくても集計が決定的になる）。
 */

export const DRAWS_KEY = '_draws';
export const OUTCOME_KEY = '_outcome';
export const TOTAL_DATE = 'total';

export const LOTTERY_EVENT_STATUS_LABELS: Readonly<Record<LotteryEventStatus, string>> = {
  active: '受付中',
  paused: '一時停止',
};

/**
 * 見出しに出す「いまの局面」。設定の status（active / paused）に抽選期間を重ねる。
 * status だけを出すと、期間前でも「受付中」と見えて誤解する（prod 確認 2026-10-28）。
 * 一時停止は期間に関係なく優先（Callable も paused を先に拒否する）。
 */
export type LotteryEventPhase = 'before' | 'open' | 'ended' | 'paused';

export const LOTTERY_EVENT_PHASE_LABELS: Readonly<Record<LotteryEventPhase, string>> = {
  before: '受付前',
  open: '受付中',
  ended: '受付終了',
  paused: '一時停止',
};

/** Chip の色。受付中だけ success、受付前・終了は中立、一時停止は注意。 */
export const LOTTERY_EVENT_PHASE_TONES: Readonly<
  Record<LotteryEventPhase, 'success' | 'neutral' | 'warning'>
> = {
  before: 'neutral',
  open: 'success',
  ended: 'neutral',
  paused: 'warning',
};

/** JST ISO8601 の辞書順比較（Callable の期間判定と同じ）。 */
export function resolveLotteryEventPhase(
  event: Pick<LotteryEvent, 'status' | 'startAt' | 'endAt'>,
  nowIso: string,
): LotteryEventPhase {
  if (event.status !== 'active') return 'paused';
  if (nowIso < event.startAt) return 'before';
  if (nowIso > event.endAt) return 'ended';
  return 'open';
}

/** `_outcome` doc のキー（表示順）。Callable の結果コードと同じ綴り。 */
export const LOTTERY_OUTCOME_KEYS = [
  'used_by_other',
  'daily_limit',
  'out_of_period',
  'invalid_code',
  'paused',
] as const;
export type LotteryOutcomeKey = (typeof LOTTERY_OUTCOME_KEYS)[number];

export const LOTTERY_OUTCOME_LABELS: Readonly<Record<LotteryOutcomeKey, string>> = {
  used_by_other: '他人の券',
  daily_limit: '本日の上限',
  out_of_period: '期間外',
  invalid_code: '無効なコード',
  paused: '一時停止',
};

export interface LotteryDrawCounts {
  count: number;
  win: number;
}

export interface LotteryPrizeRow {
  id: string;
  grade: number;
  title: string;
  todayWins: number;
  /** 本日の日別上限（dailyCaps に無い日は 0）。 */
  todayCap: number;
  totalWins: number;
  total: number;
  /** 総数 − 累計当選（負にはしない）。 */
  remaining: number;
}

export interface LotteryOutcomeRow {
  key: LotteryOutcomeKey;
  label: string;
  today: number;
  total: number;
}

export interface LotteryStatusSummary {
  today: LotteryDrawCounts;
  total: LotteryDrawCounts;
  prizes: LotteryPrizeRow[];
  outcomes: LotteryOutcomeRow[];
}

/**
 * 複数の lottery_events から「最新（createdAt が最大）」の 1 件を選ぶ。
 * createdAt は JST ISO8601 なので辞書順比較で足りる。空なら null。
 *
 * 基準は Callable（functions/src/promo/lotteryDraw.ts の bumpInvalidCodeOutcome）と
 * 同じ createdAt 最大に固定する。updatedAt にすると、古いイベントを後から編集しただけで
 * ページの対象が切り替わり、Callable が invalid_code を積む eventId と食い違う
 * （ページの「無効なコード」が 0 のままになる）。
 */
export function pickLatestLotteryEvent(
  events: readonly LotteryEvent[],
): LotteryEvent | null {
  let latest: LotteryEvent | null = null;
  for (const e of events) {
    if (!latest || e.createdAt > latest.createdAt) latest = e;
  }
  return latest;
}

function findStock(
  docs: readonly LotteryStockDoc[],
  prizeId: string,
  date: string,
): LotteryStockDoc | undefined {
  return docs.find((d) => d.prizeId === prizeId && d.date === date);
}

function drawCounts(doc: LotteryStockDoc | undefined): LotteryDrawCounts {
  return { count: doc?.count ?? 0, win: doc?.win ?? 0 };
}

function clampNonNegative(n: number): number {
  return n < 0 ? 0 : n;
}

export function summarizeLotteryStatus(
  event: LotteryEvent,
  stock: readonly LotteryStockDoc[],
  today: DateString,
): LotteryStatusSummary {
  const prizes: LotteryPrizeRow[] = [...event.prizes]
    .sort((a, b) => a.grade - b.grade)
    .map((p) => {
      const totalWins = findStock(stock, p.id, TOTAL_DATE)?.count ?? 0;
      return {
        id: p.id,
        grade: p.grade,
        title: p.title,
        todayWins: findStock(stock, p.id, today)?.count ?? 0,
        todayCap: p.dailyCaps[today] ?? 0,
        totalWins,
        total: p.total,
        remaining: clampNonNegative(p.total - totalWins),
      };
    });

  // `_outcome` は日別 doc しか無い（total doc は書かれない）ので累計はここで合算する。
  const outcomeDocs = stock.filter((d) => d.prizeId === OUTCOME_KEY);
  const outcomes: LotteryOutcomeRow[] = LOTTERY_OUTCOME_KEYS.map((key) => ({
    key,
    label: LOTTERY_OUTCOME_LABELS[key],
    today: outcomeDocs.find((d) => d.date === today)?.[key] ?? 0,
    total: outcomeDocs.reduce((sum, d) => sum + (d[key] ?? 0), 0),
  }));

  return {
    today: drawCounts(findStock(stock, DRAWS_KEY, today)),
    total: drawCounts(findStock(stock, DRAWS_KEY, TOTAL_DATE)),
    prizes,
    outcomes,
  };
}

/**
 * 上位利用者の表示名。users/line:<id> の displayName が取れなかったときは
 * lineUserId の末尾 6 桁（個人を特定しにくく、運用者が照合できる最小限）。
 */
export function fallbackLotteryUserLabel(lineUserId: string): string {
  return `…${lineUserId.slice(-6)}`;
}
